import Box from "@/ui/atoms/Box";

import type { CustomCellRendererProps, RowNode } from "@/ui/molecules/Grid";

import type {
    FileExtractionInfoWithParent,
    FileExtractionInfo,
    FileInfoWithParent,
} from "../dealTypes";

export const testIds = {
    nameLink: "FileNameCellRenderer-nameLink",
    menuButton: "FileNameCellRenderer-menuButton",
};

type ExtendedCellRendererProps<FileExtractionInfo> =
    CustomCellRendererProps<FileExtractionInfo> & {
        getExtractionUrl: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
            extractionStatus: "draft" | "completed";
        }) => string;
        getRentRollExtractionUrl: (params: {
            fileId: number;
            extractionId: number;
            sheetId: string;
            databaseId: number;
        }) => string;
        onViewExtraction: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
            extractionStatus: "draft" | "completed";
        }) => void;
        onViewRentRollExtraction: (params: {
            fileId: number;
            extractionId: number;
            sheetId: string;
            databaseId: number;
        }) => void;
    };

const isFileInfoWithParentTypeGuard = (
    file: FileExtractionInfo,
): file is FileInfoWithParent => {
    return "isParent" in file && file.isParent && !("extractionId" in file);
};

const isExtractionInfoWithParentTypeGuard = (
    file: FileExtractionInfo,
): file is FileExtractionInfoWithParent => {
    return "extractionId" in file;
};

export const FileNameCellRenderer: React.FC<
    ExtendedCellRendererProps<FileExtractionInfo>
> = ({
    onViewExtraction,
    getExtractionUrl,
    getRentRollExtractionUrl,
    onViewRentRollExtraction,
    api,
    column,
    node,
    value,
}) => {
    const getUrl = (data: FileExtractionInfoWithParent) => {
        if (data.extractionType !== "rent_roll_extraction") {
            return getExtractionUrl({
                projectId: data.projectId,
                databaseId: data.databaseId,
                extractionId: data.extractionId,
                extractionStatus: data.status as "draft" | "completed",
            });
        } else {
            if (
                data.excelsiorFileId === null ||
                data.excelsiorFileId === undefined
            ) {
                return "#";
            }
            return getRentRollExtractionUrl({
                fileId: data.excelsiorFileId,
                extractionId: Math.abs(data.extractionId),
                sheetId: `s${data.sheetId}`,
                databaseId: data.databaseId,
            });
        }
    };
    const extractionUrl =
        node.level === 1 &&
        (node.data as FileExtractionInfoWithParent | undefined)
            ?.extractionId !== undefined
            ? node.data
                ? getUrl(node.data as FileExtractionInfoWithParent)
                : "#"
            : "#";

    const onMenuClick = (event: React.MouseEvent) => {
        event.preventDefault();
        api.showContextMenu({
            value: node.data,
            x: event.clientX,
            y: event.clientY,
            column: column,
            rowNode: node as RowNode,
        });
    };

    const handleCellClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (
            node.level === 1 &&
            node.data &&
            isExtractionInfoWithParentTypeGuard(node.data)
        ) {
            if (node.data.extractionType !== "rent_roll_extraction") {
                onViewExtraction({
                    projectId: node.data.projectId,
                    databaseId: node.data.databaseId,
                    extractionId: node.data.extractionId,
                    extractionStatus: node.data.status as "draft" | "completed",
                });
            } else if (
                node.data.extractionType === "rent_roll_extraction" &&
                node.data.excelsiorFileId !== null &&
                node.data.excelsiorFileId !== undefined
            ) {
                onViewRentRollExtraction({
                    fileId: node.data.excelsiorFileId,
                    extractionId: Math.abs(node.data.extractionId),
                    sheetId: `s${node.data.sheetId}`,
                    databaseId: node.data.databaseId,
                });
            }
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem",
            }}
        >
            <Box
                sx={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                }}
                style={{ cursor: "pointer" }}
            >
                <a
                    href={extractionUrl}
                    onClick={handleCellClick}
                    data-testid={testIds.nameLink}
                    data-extractionid={
                        (node.data as FileExtractionInfoWithParent | undefined)
                            ?.extractionId
                    }
                >
                    {value}
                </a>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                }}
            >
                {node?.data && isFileInfoWithParentTypeGuard(node.data) ? (
                    <Box
                        data-testid={testIds.menuButton}
                        onClick={onMenuClick}
                        sx={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                        }}
                    >
                        ⋮
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};
